import {type FirebaseApp} from "firebase/app";
import {
  type Analytics as FirebaseAnalytics,
  getAnalytics,
  logEvent,
  setUserProperties,
  setUserId,
} from "firebase/analytics";
import {
  AirlineOrder,
  AirlinePricing,
  Attribute,
  PackingSlip,
  PackingSlipPackage,
  PrintingOrderSupplier,
} from "@/data/entities";

/**
 * Represents the analytics service for Firebase.
 */
export class Analytics {
  /**
   * The singleton instance of the Analytics class.
   */
  static _instance?: Analytics;

  /**
   * The Firebase analytics service.
   */
  service: FirebaseAnalytics;

  /**
   * Creates an instance of the Analytics class.
   * @param app - The Firebase app instance.
   */
  constructor(app: FirebaseApp) {
    this.service = getAnalytics(app);
  }

  /**
   * Returns the singleton instance of the Analytics class.
   * If the instance does not exist, it creates a new one.
   * @param app - The Firebase app instance.
   * @returns The singleton instance of the Analytics class.
   */
  static getInstance(app: FirebaseApp): Analytics {
    if (this._instance !== undefined) {
      return this._instance;
    }
    return new Analytics(app);
  }

  /**
   * Logs an event with the provided event name and parameters.
   * @param eventName - The name of the event.
   * @param eventParams - The parameters of the event.
   */
  logEvent(eventName: string, eventParams?: Record<string, unknown>): void {
    try {
      logEvent(this.service, eventName, eventParams);
    } catch (error) {
      console.error("Error logging event:", error);
    }
  }

  /**
   * Sets a user ID to a given value.
   * @param userId - The user ID.
   * @returns The user ID.
   */
  setUserId(userId: string): void {
    try {
      setUserId(this.service, userId);
    } catch (error) {
      console.error("Error setting user ID:", error);
    }
  }

  /**
   * Sets a user property to a given value.
   * @param propertyName - The name of the user property.
   * @param propertyValue - The value of the user property.
   */
  setUserProperty(propertyName: string, propertyValue: unknown): void {
    try {
      setUserProperties(this.service, {[propertyName]: propertyValue});
    } catch (error) {
      console.error("Error logging event:", error);
    }
  }
}

export function useAnalytics(app: FirebaseApp): Analytics {
  return Analytics.getInstance(app);
}

export const event = Object.freeze({
  Administration: Object.freeze({
    Created: "administration_created",
    Updated: "administration_updated",
    Deleted: "administration_deleted",
  }),
  AirlineOrder: Object.freeze({
    Created: "airline_order_created",
    Updated: "airline_order_updated",
    Deleted: "airline_order_deleted",
  }),
  AirlineOrderGrouping: Object.freeze({
    Created: "airline_order_grouping_created",
    Updated: "airline_order_grouping_updated",
    Deleted: "airline_order_grouping_deleted",

    Calculated: "airline_order_grouping_calculated",
    Submitted: "airline_order_grouping_submitted",
  }),
  AirlineSettings: Object.freeze({
    Created: "airline_settings_created",
    Updated: "airline_settings_updated",
    Deleted: "airline_settings_deleted",
  }),
  AirlinePricing: Object.freeze({
    Created: "airline_pricing_created",
    Updated: "airline_pricing_updated",
    Deleted: "airline_pricing_deleted",
  }),
  PrintingOrder: Object.freeze({
    Created: "printing_order_created",
    Updated: "printing_order_updated",
    Deleted: "printing_order_deleted",

    AddedArtwork: "printing_order_added_artwork",
    AddedOrderConfirmation: "printing_order_added_order_confirmation",
    Approved: "printing_order_approved",
    AddedTrackAndTrace: "printing_order_added_track_and_trace",
  }),
  PrintingOrderSupplier: Object.freeze({
    Created: "printing_order_supplier_created",
    Updated: "printing_order_supplier_updated",
    Deleted: "printing_order_supplier_deleted",
  }),
  PackingSlip: Object.freeze({
    Created: "packing_slip_created",
    Updated: "packing_slip_updated",
    Deleted: "packing_slip_deleted",

    Pushed: "packing_slip_pushed",
    Printed: "packing_slip_printed",
    PrintedLabel: "packing_slip_printed_label",
  }),
  PackingSlipPackage: Object.freeze({
    Created: "packing_slip_package_created",
    Updated: "packing_slip_package_updated",
    Deleted: "packing_slip_package_deleted",
  }),
  Product: Object.freeze({
    Created: "product_created",
    Updated: "product_updated",
    Deleted: "product_deleted",
  }),
  Attribute: Object.freeze({
    Created: "attribute_created",
    Updated: "attribute_updated",
    Deleted: "attribute_deleted",
  }),
  PriceLayer: Object.freeze({
    Created: "price_layer_created",
    Updated: "price_layer_updated",
    Deleted: "price_layer_deleted",
  }),
  UserProfile: Object.freeze({
    Created: "user_profile_created",
    Updated: "user_profile_updated",
    Deleted: "user_profile_deleted",
  }),
  PageView: "page_view",
  SignIn: "sign_in",
  SignOut: "sign_out",
  SignUp: "sign_up",
  PasswordResetVerified: "password_reset_verified",
  PasswordResetRequested: "password_reset_requested",
  PasswordResetConfirmed: "password_reset_confirmed",
});
